/**
 * @param title 标题，默认 未来智能
 * @param hideTab 是否隐藏标签页，默认 false
 * @param menu 当前菜单
 * @param custom 自定义标题，用于动态路由标题，默认false
 * @param back 首页是否显示返回按钮，默认false
 */

import { createRouter, createWebHashHistory, createWebHistory } from 'vue-router'
import { useMenuStore } from '@/store/menu'
import { useInternationalStore } from '@/store/international'
import { useCookieStore } from '@/store/cookie'
import { getLocalizedMeta, getSupportLocalizedMeta, isMode, getBrowserLanguage, isKoRoute } from '@/utils/common'
import i18n from '@/i18n'
// import axios from 'axios'
import ModeType from '@/enum/ModeType'

// 有头部和底部的布局
const Layout = () => import('@/layout/index.vue')
// 无头部和底部的布局
const SimpleLayout = () => import('@/layout/Simple.vue')
// Security页面私有布局
const SecurityLayout = () => import('@/views/security/components/SecurityLayout.vue')
const embeddedRoutePrefixes = ['nohead', 'embed']
const embeddedRootPaths = ['download', 'viaimai', 'support']

const getEmbeddedRouteNamePrefix = (prefix: string) => prefix === 'nohead' ? 'Nohead' : 'Embed'

const createEmbeddedRoutes = (prefix: string) => {
  const routeNamePrefix = getEmbeddedRouteNamePrefix(prefix)

  return [
    {
      name: `${routeNamePrefix}Download`,
      path: `/:language?/${prefix}/download`,
      component: SimpleLayout,
      redirect: (to: any) => `/${[to.params.language, prefix, 'download', 'index'].filter(Boolean).join('/')}`,
      children: [
        {
          name: `${routeNamePrefix}DownloadIndex`,
          path: 'index',
          component: () => import('@/views/download/index.vue'),
          meta: {
            hideTab: false,
            menu: '',
            ...getLocalizedMeta('download')
          }
        }
      ]
    },
    {
      name: `${routeNamePrefix}Viaim`,
      path: `/:language?/${prefix}/viaimai`,
      component: SimpleLayout,
      redirect: (to: any) => `/${[to.params.language, prefix, 'viaimai', 'index'].filter(Boolean).join('/')}`,
      children: [
        {
          name: `${routeNamePrefix}ViaimaiIndex`,
          path: 'index',
          component: () => import('@/views/viaimai/Index.vue'),
          meta: {
            hideTab: false,
            menu: '',
            ...getLocalizedMeta('viaimAi')
          }
        }
      ]
    },
    {
      name: `${routeNamePrefix}Support`,
      path: `/:language?/${prefix}/support`,
      component: SimpleLayout,
      redirect: (to: any) => `/${[to.params.language, prefix, 'support', 'index'].filter(Boolean).join('/')}`,
      children: [
        {
          name: `${routeNamePrefix}SupportIndex`,
          path: 'index',
          component: () => import('@/views/support/index.vue'),
          meta: {
            hideTab: false,
            menu: '',
            ...getLocalizedMeta('support')
          }
        },
        {
          name: `${routeNamePrefix}SupportProduct`,
          path: 'product/:type',
          component: () => import('@/views/support/pages/nanoAir/index.vue'),
          meta: {
            title: '',
            hideTab: false,
            menu: '',
            custom: true
          }
        },
        {
          name: `${routeNamePrefix}ProductSeeMore`,
          path: 'seeMore/:product/:id?',
          component: () => import('@/views/support/pages/seeMore/index.vue'),
          meta: {
            title: '',
            hideTab: false,
            menu: ''
          }
        },
        {
          name: `${routeNamePrefix}ProductDetail`,
          path: 'detail/:id/:type?',
          component: () => import('@/views/support/pages/detail/Detail.vue'),
          meta: {
            title: '',
            hideTab: false,
            menu: '',
            back: true
          }
        },
        {
          name: `${routeNamePrefix}VideoList`,
          path: 'videoList/:product/:type?',
          component: () => import('@/views/support/pages/seeMore/pages/videoList.vue'),
          meta: {
            title: '',
            hideTab: false,
            menu: ''
          }
        },
        {
          name: `${routeNamePrefix}WarrantyPolicy`,
          path: 'WarrantyPolicy',
          component: () => import('@/views/support/pages/WarrantyPolicy/Index.vue'),
          meta: {
            hideTab: false,
            menu: '',
            ...getLocalizedMeta('warrantyPolicy')
          }
        }
      ]
    }
  ]
}

const getEmbeddedPrefixFromPath = (path: string) => path.split('/').find(segment => embeddedRoutePrefixes.includes(segment))

const normalizeEmbeddedPath = (path: string) => {
  const pathSegments = path.split('/').filter(Boolean)
  const embeddedPrefixIndex = pathSegments.findIndex(segment => embeddedRoutePrefixes.includes(segment))
  const rootPathIndex = pathSegments.findIndex(segment => embeddedRootPaths.includes(segment))

  if (embeddedPrefixIndex >= 0) return `/${pathSegments.slice(embeddedPrefixIndex + 1).join('/')}`
  if (rootPathIndex >= 0) return `/${pathSegments.slice(rootPathIndex).join('/')}`

  return path
}

const addEmbeddedPrefix = (fullPath: string, prefix: string) => {
  const [, path = '', suffix = ''] = fullPath.match(/^([^?#]*)(.*)$/) || []

  if (getEmbeddedPrefixFromPath(path)) return fullPath

  const pathSegments = path.split('/').filter(Boolean)
  const rootPathIndex = pathSegments.findIndex(segment => embeddedRootPaths.includes(segment))

  if (rootPathIndex < 0) return fullPath

  const languageSegments = pathSegments.slice(0, rootPathIndex)
  const contentSegments = pathSegments.slice(rootPathIndex)

  return `/${[...languageSegments, prefix, ...contentSegments].join('/')}${suffix}`
}

const updateRouteLocale = (path: string) => {
  i18n.global.locale.value = isKoRoute(path) ? 'ko' : import.meta.env.VITE_PAGE_LANGUAGE
}

// 导入静态路由
const modules: Record<string, any> = import.meta.glob(['./modules/**/*.ts'], {
  eager: true
})

// 原始静态路由
const routes: any[] = [
  {
    path: '/:language?',
    name: 'layout',
    component: Layout,
    redirect: '/home',
    children: [
      {
        path: 'home',
        name: 'home',
        component: () => import('@/views/home/index.vue'),
        meta: getLocalizedMeta('home')
      }
    ]
  },
  ...createEmbeddedRoutes('nohead'),
  ...createEmbeddedRoutes('embed'),
  // Security页面路由
  {
    name: 'Security',
    path: '/:language?/security',
    component: SecurityLayout,
    meta: {
      hideTab: false,
      menu: '',
      ...getLocalizedMeta('security')
    }
  },
]

Object.keys(modules).forEach(key => {
  routes.push(modules[key].default)
})
const router = createRouter({
  history: import.meta.env.VITE_ROUTER_MODE === 'hash' ? createWebHashHistory() : createWebHistory(import.meta.env.VITE_ROUTER_PATH || '/'),
  routes: routes
})

/** 是否已经获取IP位置 */
// let isGetLocation = false

/** 获取IP位置 */
// function getLocation() {
//   return new Promise((resolve, reject) => {
//     try {
//       axios({
//         method: 'get',
//         url: '/clientbizservice/user/other/ipLocation'
//       })
//         .then(res => {
//           if (res.data.result) {
//             resolve(res.data.result.abb)
//           } else {
//             console.log('res: ', res)
//             resolve(isMode('jp') ? 'jp' : 'en')
//           }
//         })
//         .catch(err => {
//           reject(isMode('jp') ? 'jp' : 'en')
//         })
//     } catch (err) {
//       reject(isMode('jp') ? 'jp' : 'en')
//     }
//   })
// }

router.beforeEach((to, from, next) => {
  // 控制无需头部和底部的路由跳转
  const embeddedPrefix = getEmbeddedPrefixFromPath(from.path)
  if (embeddedPrefix) {
    // 需添加无头布局前缀的原路径列表
    const embeddedPathList = [
      '/download/index',
      '/viaimai/index',
      '/support/product/',
      '/support/WarrantyPolicy',
      '/support/index',
      '/support/seeMore/',
      '/support/videoList/',
      '/support/detail/'
    ]
    // 检查是否需要添加前缀
    const normalizedPath = normalizeEmbeddedPath(to.path)
    const isShouldAddPrefix = embeddedPathList.some(item => normalizedPath === item || normalizedPath.startsWith(item))
    if (isShouldAddPrefix) {
      const targetPath = addEmbeddedPrefix(to.fullPath, embeddedPrefix)
      if (targetPath !== to.fullPath) {
        next(targetPath)
        return
      }
    }
  }

  const internationalStore = useInternationalStore()
  updateRouteLocale(to.path)

  if (isMode(ModeType.PROD) || isMode(ModeType.DEV)) {
    const browserLanguage = getBrowserLanguage()
    const oldHash = window.location.hash
    let targetPath = ''
    // ja语言处理
    if (browserLanguage === 'ja') {
      if (to.fullPath.startsWith('/jp/') || to.fullPath.startsWith('/en/')) {
        targetPath = to.fullPath
      } else {
        targetPath = `/jp${to.fullPath}`
      }
    } 
    // 非ja语言，但存在hash路径#号的情况
    else if (oldHash.startsWith('#/jp/') || oldHash.startsWith('#/en/')) {
      targetPath = oldHash.substring(1) // 去除#号
    }
    if (targetPath) {
      const url = `${window.location.origin}${targetPath}`
      window.location.href = url
      return
    }

    // 访问根节点时，逻辑处理
    if (to.fullPath === '/home') {
      window.location.href = browserLanguage === 'ar' ? import.meta.env.VITE_SITE_URL_AR : import.meta.env.VITE_SITE_URL_EN
      return
    }
  }

  // 在路由跳转前先隐藏CookieBanner
  const cookieStore = useCookieStore()
  if (!cookieStore.hasUserConsent) {
    cookieStore.hideBanner()
  }

  if (to.matched.length === 0) {
    from.name ? next({ name: from.name }) : next('/')
  } else {
    const menuStore = useMenuStore()
    menuStore.SetMenuSelect(to.meta.menu ?? '')
    if (to.params.language !== '') next(`/${internationalStore.location.abb}${to.fullPath}`)
    else next()
  }
})

router.afterEach(to => {
  window.scrollTo({ top: 0 })

  const description: any = document.getElementById('shareContent')
  if (to.meta.custom) {
    description.content = getSupportLocalizedMeta(to.path).description()
  } else {
    // 处理 description，可能是函数或字符串
    let descriptionText = 'viaim'
    if (typeof to.meta.description === 'function') {
      descriptionText = (to.meta.description as Function)()
    } else if (to.meta.description) {
      descriptionText = to.meta.description as string
    }
    description.content = descriptionText
  }

  const title: any = document.getElementById('title')
  if (to.meta.custom) {
    title.innerText = getSupportLocalizedMeta(to.path).title()
  } else {
    // 处理 title，可能是函数或字符串
    let titleText = 'viaim'
    if (typeof to.meta.title === 'function') {
      titleText = (to.meta.title as Function)()
    } else if (to.meta.title) {
      titleText = to.meta.title as string
    }
    title.innerText = titleText
  }

  const image: any = document.getElementById('shareImage')
  if (to.meta.image) image.content = to.meta.image || 'https://viaim-h5.oss-ap-southeast-1.aliyuncs.com/iflybuds-website-world/images/logo.png'
  else image.content = 'https://viaim-h5.oss-ap-southeast-1.aliyuncs.com/iflybuds-website-world/images/logo.png'

  // 路由变化完成后重新显示CookieBanner（如果用户还没有同意）
  const cookieStore = useCookieStore()
  if (!cookieStore.hasUserConsent) {
    // 延迟显示，确保页面内容加载完成后再显示banner
    const timer = setTimeout(() => {
      cookieStore.showBanner()
      clearTimeout(timer)
    }, 500)
  }
})

export default router
